import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Package } from 'lucide-react';

const StoreSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [order, setOrder] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        toast.error('Payment verification timed out');
        return;
      }
      
      try {
        const response = await api.get(`/store/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrder(response.data.order);
          localStorage.removeItem('cart');
          toast.success('Order confirmed!');
          return;
        }
        
        attempts++;
        setTimeout(poll, 2000);
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          setStatus('error');
          toast.error('Failed to verify payment');
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-8">
        {status === 'checking' && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-heading font-bold mb-2" data-testid="order-checking-status">Verifying Order...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </div>
        )}
        
        {status === 'success' && order && (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2" data-testid="order-success-status">Order Confirmed!</h2>
            <p className="text-muted-foreground mb-6">Thank you for your purchase.</p>
            
            <div className="bg-background/50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center mb-3">
                <Package className="w-5 h-5 text-primary mr-2" />
                <span className="font-semibold">Order Items:</span>
              </div>
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm mb-2">
                  <span>{item.name} x{item.quantity}</span>
                  <span>₦{item.subtotal.toLocaleString()}</span>
                </div>
              ))}
              <div className="border-t border-white/10 mt-3 pt-3 flex justify-between font-bold">
                <span>Total:</span>
                <span>₦{order.total_amount.toLocaleString()}</span>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/store')}
              className="w-full bg-primary hover:bg-primary/90 mb-3"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold mb-2 text-destructive">Verification Failed</h2>
            <p className="text-muted-foreground mb-6">We couldn't verify your payment. Please contact support.</p>
            <Button
              onClick={() => navigate('/store')}
              variant="outline"
              className="w-full"
            >
              Back to Store
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreSuccess;